// material
import {
  Container,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Box,
  Dialog,
  Slide,
  DialogTitle,
  DialogActions,
  DialogContentText
} from '@mui/material';
// hooks
import useSettings from '../../hooks/useSettings';
// components
import Page from '../../components/Page';
import HeaderBreadcrumbs from '../../components/HeaderBreadcrumbs';
import { PATH_DASHBOARD } from '../../routes/paths';
import { checkRoles, ROLE_USER } from 'guards/CheckRoles';
import { useTranslation } from 'react-i18next';
import SvgIconStyle from '../../components/SvgIconStyle';
import LoadingSpinner from '../../components/LoadingSpinner';
import { RootState, useSelector } from '../../redux/store';
import * as React from 'react';
import { useState } from 'react';
import { deliveryRequestState, getDeliveryRequests } from 'recoil/deliveryRequestState';
import { useSetRecoilState, useRecoilValue } from 'recoil';
import { DeliveryRequest } from '../../@types/model/deliveryRequest';
import { DATE_TIME_FORMAT_SHORT, show_date } from '../../@types/model/device';
import axios from '../../utils/axios';
import { MIconButton } from 'components/@material-extend';
import { TransitionProps } from '@mui/material/transitions';
import { DialogContent } from '@mui/material';
import closeFill from '@iconify/icons-eva/close-fill';
import { Icon } from '@iconify/react';
import { useSnackbar } from 'notistack';
import { LoadingButton } from '@mui/lab';

const Transition: any = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});
// ----------------------------------------------------------------------

export default function DeliveryRequestList() {
  const { themeStretch } = useSettings();
  const { t } = useTranslation();
  const { user } = useSelector((state: RootState) => state.user);
  const [loading] = useState(false);
  const [deliveryRequestToDelete, setDeliveryRequestToDelete] = useState<DeliveryRequest | null>(
    null
  );
  const [loadingDelete, setLoadingDelete] = useState(false);
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const deliveryRequest: DeliveryRequest[] = useRecoilValue(deliveryRequestState);
  const setDeliveryRequest = useSetRecoilState(deliveryRequestState);

  const role = checkRoles(user, ROLE_USER) ? '/' : '/end-user';

  React.useEffect(() => {
    getDeliveryRequests().then((result: any) => {
      result.deliveryRequests = result.deliveryRequests.map((dr: DeliveryRequest) => {
        let total = 0;
        let n_slots = 0;
        dr?.items?.forEach((item: any) => {
          if (item.quantity) {
            n_slots += item.quantity;
            total += 1;
          }
        });
        return {
          ...dr,
          total: total,
          n_slots: n_slots
        };
      });
      setDeliveryRequest(result.deliveryRequests);
    });
  }, []);

  function deleteDeliveryRequest(dr: DeliveryRequest | null) {
    setLoadingDelete(true);
    axios
      .delete('/api/v2/delivery-request/' + dr?.guid)
      .then(() => {
        enqueueSnackbar(t('delivery_request_deleted'), {
          variant: 'success',
          action: (key: any) => (
            <MIconButton size="small" onClick={() => closeSnackbar(key)}>
              <Icon icon={closeFill} />
            </MIconButton>
          )
        });
        setDeliveryRequest(
          deliveryRequest.filter((deliveryRequest: DeliveryRequest) => {
            if (dr?.guid === deliveryRequest.guid) return false;
            return true;
          })
        );
        setLoadingDelete(false);
        setDeliveryRequestToDelete(null);
      })
      .catch((e: any) => {
        enqueueSnackbar(e.detail, {
          variant: 'error',
          action: (key: any) => (
            <MIconButton size="small" onClick={() => closeSnackbar(key)}>
              <Icon icon={closeFill} />
            </MIconButton>
          )
        });
        setLoadingDelete(false);
      });
  }

  const pageTitle = t('delivery_requests');
  return (
    <Page title={pageTitle}>
      <Container maxWidth={themeStretch ? false : 'xl'}>
        <HeaderBreadcrumbs
          heading={pageTitle}
          links={[
            { name: t('home'), href: role },
            { name: pageTitle, href: PATH_DASHBOARD.deliveryConfigurations.list }
          ]}
        />
        {loading ? (
          <LoadingSpinner />
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                {user?.attributes.roles.includes('ROLE_USER') && <TableCell>{t('user')}</TableCell>}
                <TableCell>{t('delivery_configuration')}</TableCell>
                <TableCell>{t('status')}</TableCell>
                <TableCell>{t('total_load')}</TableCell>
                <TableCell>{t('silos')}</TableCell>
                <TableCell>{t('delivery_date')}</TableCell>
                <TableCell>{t('created_date')}</TableCell>
                <TableCell>{t('message')}</TableCell>
                {user?.attributes.roles.includes('ROLE_USER') && (
                  <TableCell>{t('options')}</TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {deliveryRequest.map((dr: DeliveryRequest) => {
                return (
                  <TableRow key={dr.guid}>
                    {user?.attributes.roles.includes('ROLE_USER') && (
                      <TableCell>{dr?.user_data?.name}</TableCell>
                    )}
                    <TableCell>{dr?.delivery_configuration_data?.configuration?.name}</TableCell>
                    <TableCell>
                      <Box
                        component="span"
                        sx={{
                          display: 'inline-block',
                          padding: '2px 8px',
                          borderRadius: '4px',
                          color:
                            dr.state === 'deleted'
                              ? 'white'
                              : dr.state === 'created'
                              ? 'black'
                              : 'white',
                          backgroundColor:
                            dr.state === 'deleted'
                              ? 'red'
                              : dr.state === 'created'
                              ? '#ffbe00'
                              : 'green'
                        }}
                      >
                        {t(dr.state)}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {/* {(dr?.n_slots ?? 0) *
                        (dr?.delivery_configuration_data?.configuration?.slot_amount ?? 0)} */}
                      {dr.items
                        ?.map((i: any) => i.attributes.quantity)
                        .reduce((partialSum, a) => partialSum + a, 0)}
                      Kg
                    </TableCell>
                    <TableCell>
                      {dr?.items
                        ?.map((item) => {
                          return item?.device_data?.attributes?.name ?? item?.device;
                        })
                        .join(', ')}
                    </TableCell>
                    <TableCell>{show_date(dr.delivery_date, DATE_TIME_FORMAT_SHORT)}</TableCell>
                    <TableCell>{show_date(dr.created_at, DATE_TIME_FORMAT_SHORT)}</TableCell>
                    <TableCell>{dr?.message}</TableCell>
                    {user?.attributes.roles.includes('ROLE_USER') && (
                      <TableCell>
                        <IconButton
                          onClick={() => {
                            setDeliveryRequestToDelete(dr);
                          }}
                        >
                          <SvgIconStyle
                            src="/static/icons/giraf/remove.svg"
                            sx={{
                              width: '20px',
                              height: '20px',
                              color: 'red'
                            }}
                          />
                        </IconButton>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}

        <Dialog
          open={deliveryRequestToDelete !== null}
          TransitionComponent={Transition}
          keepMounted
          onClose={() => {
            setDeliveryRequestToDelete(null);
          }}
          aria-describedby="alert-dialog-slide-description"
        >
          <DialogTitle>{t('delivery_request_confirm')}</DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-slide-description">
              {t('delivery_request_delete_confirm_dialog')}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setDeliveryRequestToDelete(null);
              }}
            >
              {t('cancel')}
            </Button>
            <LoadingButton
              variant={'contained'}
              loading={loadingDelete}
              color="error"
              onClick={() => {
                deleteDeliveryRequest(deliveryRequestToDelete);
              }}
            >
              {t('delete')}
            </LoadingButton>
          </DialogActions>
        </Dialog>
      </Container>
    </Page>
  );
}
